import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { productCenterContent } from '@/mocks/products';

const platformLayers = [
  {
    title: 'AI 大模型层',
    items: [
      '模型能力：DeepSeek共用模型/银行私有模型',
      '智能调度：Agent/Skill管理与调度',
      '智能算法：OCR视觉识别、链路学习',
      '密码加密解密：凭据保护、安全调用',
    ],
    icon: 'ri-brain-line',
  },
  {
    title: '虚拟机平台层',
    items: [
      '资源管控：主机连接与虚机资源配置',
      '链路监控：实时链路状态与故障排查',
      '银行镜像：银行环境动态更新与维护',
    ],
    icon: 'ri-server-line',
  },
  {
    title: '网银 Agent',
    items: [
      '训练器：Agent技能训练与迭代',
      '智能运维：自动运维与异常处置',
      '执行管控：任务执行全流程监控',
      '多功能Skill：登录/查询/转账/对账',
    ],
    icon: 'ri-robot-2-line',
  },
  {
    title: '深度业财应用',
    items: ['流水下载、回单下载', '资金日报、电子档案', '智能支付、智能对账'],
    icon: 'ri-bar-chart-line',
  },
  {
    title: 'IoT 设备层',
    items: [
      'USB Server：多端口U盾集中管理',
      '一体按压板：物理按键自动按压',
      '智能配件：扩展设备与智能联动',
    ],
    icon: 'ri-usb-line',
  },
];

const traits = [
  { title: 'DeepSeek大模型', icon: 'ri-brain-line' },
  { title: 'Agent智能调度', icon: 'ri-git-merge-line' },
  { title: 'OCR视觉识别', icon: 'ri-eye-line' },
  { title: '密码安全调用', icon: 'ri-shield-keyhole-line' },
  { title: '流程动态更新', icon: 'ri-refresh-line' },
  { title: '链路故障排查', icon: 'ri-bug-line' },
  { title: '资源智能管控', icon: 'ri-database-2-line' },
  { title: '多功能Skill', icon: 'ri-tools-line' },
];

const highlights = [
  { label: '7*24小时', suffix: '不间断服务', icon: 'ri-time-line' },
  { label: '成本下降', value: '50%', suffix: '以上', icon: 'ri-arrow-down-circle-line' },
  { label: '效率提升', value: '5', suffix: '倍以上', icon: 'ri-speed-line' },
  { label: '无需', suffix: 'API的全连通', icon: 'ri-plug-line' },
  { label: '云+私部署速度提升', value: '3', suffix: '倍', icon: 'ri-cloud-line' },
  { label: '安全稳定性提升', value: '5', suffix: '倍', icon: 'ri-shield-star-line' },
  { label: '误差率降到原来的', value: '1/60', suffix: '', icon: 'ri-error-warning-line' },
  { label: '密码', value: '零接触', suffix: '业务人员不直接接触', icon: 'ri-lock-line' },
];

const comparison = {
  headers: ['对比内容', '析莜网银Agent', '传统RPA方案'],
  rows: [
    [
      '系统连接是否需要API接口',
      '无需API接口，部署快、成本低',
      '需要API接口，开发周期长、成本高',
    ],
    [
      '密码安全管理',
      '密码按需解密，业务人员零接触',
      '密码明文传输或存储，存在泄露风险',
    ],
    [
      '银行流程更新',
      '智能动态更新，自动适配银行变化',
      '流程固定，银行升级后易失效',
    ],
    [
      '链路故障处理',
      '智能排查与自愈，7×24小时监控',
      '故障依赖人工排查，恢复周期长',
    ],
    [
      '深度业财应用',
      '流水/回单/支付/对账全闭环',
      '仅能完成简单重复操作',
    ],
  ],
};

const stats = [
  { value: '1000+', label: '大中型客户服务经验', icon: 'ri-building-4-line' },
  { value: '500+', label: '业务咨询及实施团队', icon: 'ri-user-settings-line' },
  { value: '100+', label: '注册会计师', icon: 'ri-file-list-3-line' },
  { value: '50+', label: '内控及流程管理专家', icon: 'ri-shield-user-line' },
  { value: '10+', label: '信息安全专家', icon: 'ri-shield-keyhole-line' },
];

const ukeyFeatures = [
  {
    title: '工作台',
    items: ['设备与UKey核心指标', '挂载/按压趋势统计', '实时挂载监控'],
    icon: 'ri-dashboard-line',
  },
  {
    title: '网络管理',
    items: ['网卡配置', '静态IP信息维护', '网络配置更新确认'],
    icon: 'ri-global-line',
  },
  {
    title: '设备管理',
    items: ['设备注册与机柜位置', '24端口状态查看', '端口UKey占用监控'],
    icon: 'ri-server-line',
  },
  {
    title: '虚机管理',
    items: ['主机连接信息维护', '虚机资源与桌面配置', '执行记录与录像回放'],
    icon: 'ri-hard-drive-line',
  },
  {
    title: '数据管理',
    items: ['UKey数据维护', '网银账户维护', '账户与UKey关联'],
    icon: 'ri-database-2-line',
  },
  {
    title: '治理运维',
    items: ['人员与账户授权', '操作与UKey日志', '组织角色菜单字典'],
    icon: 'ri-shield-user-line',
  },
];

const businessFeatures = [
  {
    title: '总览',
    items: ['资金关键指标', '资金流转趋势', '维度结构分析'],
    icon: 'ri-pie-chart-line',
  },
  {
    title: '账户中心',
    items: ['UKey账户业务信息', '账户资源详情'],
    icon: 'ri-account-circle-line',
  },
  {
    title: '业务管理',
    items: ['资金日报', '资金采集账号', '流水与维度管理'],
    icon: 'ri-funds-line',
  },
  {
    title: '回单管理',
    items: ['回单列表查询', '流水详情联动', '查看与批量下载'],
    icon: 'ri-file-list-line',
  },
  {
    title: '付款管理',
    items: ['转账单管理', '新建付款单', '复核与回单下载'],
    icon: 'ri-bank-card-line',
  },
  {
    title: '任务与运维',
    items: ['余额/流水查询单', '执行监控与预警', 'AI处置与人工介入'],
    icon: 'ri-list-check-2',
  },
];

const passwordFlow = [
  { label: 'UKey管理平台', sub: '账户与UKey资源', icon: 'ri-key-2-line' },
  { label: '同步密文', sub: '本地安全存储', icon: 'ri-lock-line' },
  { label: '网银Agent', sub: '触发密码输入', icon: 'ri-robot-2-line' },
  {
    label: '按密码类型处理',
    sub: 'UKey口令/证书密码、查询密文并解密',
    icon: 'ri-shield-keyhole-line',
  },
  { label: '网银Agent', sub: '自动输入密码', icon: 'ri-robot-2-line' },
  { label: '网银/UKey窗口', sub: '完成安全登录', icon: 'ri-window-line' },
];

export default function ProductIntroDetail() {
  const product = productCenterContent.items[0];
  const otherProducts = productCenterContent.items.filter(
    (item) => item.slug !== 'intro',
  );

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Header */}
        <section className="pt-28 md:pt-36 pb-10 md:pb-14 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <nav className="mb-8 flex items-center gap-2 text-sm text-foreground-300">
              <Link to="/" className="hover:text-primary-400 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <Link
                to="/product-center"
                className="hover:text-primary-400 transition-colors"
              >
                产品中心
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-100 font-medium">
                {product.titleCn}
              </span>
            </nav>

            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
              <div className="max-w-3xl">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-500 text-foreground-50">
                    <i className={`${product.icon} text-2xl`} />
                  </div>
                  <span className="text-sm font-medium text-primary-400 tracking-wide font-label">
                    {product.titleEn}
                  </span>
                </div>
                <h1 className="text-3xl md:text-5xl font-black text-foreground-50 font-heading">
                  {product.titleCn}
                </h1>
                <p className="mt-5 text-base md:text-lg text-foreground-300 leading-relaxed max-w-2xl">
                  析莜科技以国产自研网银Agent为核心，替代传统RPA流程自动化，构建「AI大模型
                  +
                  虚拟机平台
                  +
                  网银Agent」三层架构，为企业提供安全、高效、可视、可控的第三代网银智能管理平台。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 第三代网银智能管理平台架构 */}
        <section className="pb-16 md:pb-24 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading">
                    第三代网银智能管理平台
                  </h2>
                  <div className="flex-1 h-px bg-background-200/60" />
                </div>
                <p className="text-sm md:text-base text-foreground-300 leading-loose">
                  基于AI大模型 + 虚拟机平台 + 网银Agent的三层技术架构，替代传统RPA流程自动化。AI大模型层提供DeepSeek共用模型、银行私有模型、OCR视觉识别与链路学习等核心智能能力；虚拟机平台实现资源管控、链路监控与银行镜像管理；网银Agent通过训练器、智能运维与多功能Skill完成登录、查询、转账、对账等深度业财操作。配合USB
                  Server、一体按压板等IoT设备，实现从密码安全到业务管理的全链路闭环。
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  {['AI大模型', '网银Agent', '深度业财', '密码零接触'].map(
                    (tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-300 border border-background-200/50"
                      >
                        {tag}
                      </span>
                    ),
                  )}
                </div>
              </div>
              <div className="relative rounded-lg overflow-hidden border border-background-200/50">
                <img
                  src="https://readdy.ai/api/search-image?query=Abstract%20enterprise%20AI%20agent%20architecture%20visualization%2C%20layered%20technology%20stack%20with%20neural%20network%20nodes%2C%20virtual%20machines%20and%20IoT%20devices%20connected%20in%20a%20unified%20flow%2C%20deep%20charcoal%20background%20with%20warm%20amber%20and%20muted%20teal%20accent%20tones%2C%20clean%20minimal%20geometric%20composition%2C%20modern%20corporate%20technology%20illustration%2C%20soft%20directional%20lighting%2C%20no%20glowing%20neon%2C%20no%20blue%20or%20purple%20colors%2C%20professional%20restrained%20aesthetic&width=800&height=500&seq=intro-arch-01&orientation=landscape"
                  alt="第三代网银智能管理平台架构"
                  title="第三代网银智能管理平台架构"
                  className="w-full h-64 md:h-80 object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* 平台核心架构层 */}
        <section className="bg-background-100 py-16 md:py-24">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading">
                平台核心架构层
              </h2>
              <div className="mt-4 mx-auto w-12 h-0.5 bg-primary-500 rounded-full" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {platformLayers.map((layer) => (
                <div
                  key={layer.title}
                  className="group bg-background-50 rounded-lg border border-background-200/50 p-6 md:p-8 hover:border-primary-500/40 transition-all duration-300"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-500 text-foreground-50">
                      <i className={`${layer.icon} text-xl`} />
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-foreground-50">
                      {layer.title}
                    </h3>
                  </div>
                  <ul className="space-y-2">
                    {layer.items.map((item) => (
                      <li
                        key={item}
                        className="flex items-center gap-2 text-sm text-foreground-300"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 网银Agent核心能力 */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading">
                网银Agent核心能力
              </h2>
              <div className="mt-4 mx-auto w-12 h-0.5 bg-primary-500 rounded-full" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {traits.map((trait) => (
                <div
                  key={trait.title}
                  className="group flex flex-col items-center text-center bg-background-100 rounded-lg border border-background-200/50 p-6 md:p-8 hover:border-primary-500/40 transition-all duration-300"
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-background-50 text-primary-500 mb-4 group-hover:bg-primary-500 group-hover:text-foreground-50 transition-colors">
                    <i className={`${trait.icon} text-2xl`} />
                  </div>
                  <span className="text-sm md:text-base font-semibold text-foreground-50">
                    {trait.title}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* UKEY智能管理平台 */}
        <section className="bg-background-100 py-16 md:py-24">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <div className="mb-10 md:mb-14">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading">
                网银盾智管平台
              </h2>
              <p className="mt-3 text-sm text-foreground-300">
                覆盖设备、网络、UKey、授权与系统治理的全域功能
              </p>
              <div className="mt-4 w-12 h-0.5 bg-primary-500 rounded-full" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {ukeyFeatures.map((f) => (
                <div
                  key={f.title}
                  className="group bg-background-50 rounded-lg border border-background-200/50 p-6 hover:border-primary-500/40 transition-all duration-300"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500 text-foreground-50">
                      <i className={`${f.icon} text-lg`} />
                    </div>
                    <h3 className="text-base font-bold text-foreground-50">
                      {f.title}
                    </h3>
                  </div>
                  <ul className="space-y-1.5">
                    {f.items.map((item) => (
                      <li
                        key={item}
                        className="flex items-center gap-2 text-sm text-foreground-300"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 业务管理平台 */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <div className="mb-10 md:mb-14">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading">
                网银业务智管平台
              </h2>
              <p className="mt-3 text-sm text-foreground-300">
                覆盖账户、查询、转账与执行监控的完整业务闭环
              </p>
              <div className="mt-4 w-12 h-0.5 bg-primary-500 rounded-full" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {businessFeatures.map((f) => (
                <div
                  key={f.title}
                  className="group bg-background-100 rounded-lg border border-background-200/50 p-6 hover:border-primary-500/40 transition-all duration-300"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500 text-foreground-50">
                      <i className={`${f.icon} text-lg`} />
                    </div>
                    <h3 className="text-base font-bold text-foreground-50">
                      {f.title}
                    </h3>
                  </div>
                  <ul className="space-y-1.5">
                    {f.items.map((item) => (
                      <li
                        key={item}
                        className="flex items-center gap-2 text-sm text-foreground-300"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 密码安全机制 */}
        <section className="bg-background-100 py-16 md:py-24">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading">
                网银账户密码加密及解密功能
              </h2>
              <p className="mt-3 text-sm text-foreground-300">
                密码按需查询和解密，通过网银Agent完成输入，减少业务人员直接接触密码
              </p>
              <div className="mt-4 mx-auto w-12 h-0.5 bg-primary-500 rounded-full" />
            </div>
            <div className="flex flex-col items-center gap-4 md:flex-row md:justify-center md:gap-6">
              {passwordFlow.map((step, idx) => (
                <div
                  key={`pw-${idx}`}
                  className="flex flex-col items-center gap-4 md:flex-row md:items-center md:gap-6"
                >
                  <div className="flex flex-col items-center text-center bg-background-50 rounded-lg border border-background-200/50 p-5 w-44 hover:border-primary-500/40 transition-all">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500 mb-3">
                      <i className={`${step.icon} text-xl`} />
                    </div>
                    <span className="text-sm font-semibold text-foreground-50">
                      {step.label}
                    </span>
                    <span className="text-xs text-foreground-300 mt-1">
                      {step.sub}
                    </span>
                  </div>
                  {idx < passwordFlow.length - 1 && (
                    <>
                      <div className="hidden md:flex items-center justify-center w-6 h-6 text-primary-500">
                        <i className="ri-arrow-right-line text-lg" />
                      </div>
                      <div className="flex md:hidden items-center justify-center w-6 h-6 text-primary-500">
                        <i className="ri-arrow-down-line text-lg" />
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 网银Agent应用效果 */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading">
                网银Agent应用效果
              </h2>
              <div className="mt-4 mx-auto w-12 h-0.5 bg-primary-500 rounded-full" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
              {highlights.map((h) => (
                <div
                  key={h.label}
                  className="group bg-background-100 rounded-lg border border-background-200/50 p-6 md:p-8 hover:border-primary-500/40 transition-all duration-300"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500 mb-5 group-hover:bg-primary-500 group-hover:text-foreground-50 transition-colors">
                    <i className={`${h.icon} text-xl`} />
                  </div>
                  <div className="flex flex-wrap items-baseline gap-1">
                    <span className="text-sm text-foreground-300">{h.label}</span>
                    {h.value && (
                      <span className="text-2xl md:text-3xl font-black text-primary-500 font-heading">
                        {h.value}
                      </span>
                    )}
                    <span className="text-sm text-foreground-300">{h.suffix}</span>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-8 text-xs text-foreground-400 text-center">
              注：具体视数字化项目实施场景而定
            </p>
          </div>
        </section>

        {/* 优势对比表 */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading">
                网银Agent vs 传统RPA
              </h2>
              <div className="mt-4 mx-auto w-12 h-0.5 bg-primary-500 rounded-full" />
            </div>
            <div className="overflow-x-auto rounded-lg border border-background-200/50">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-100">
                    {comparison.headers.map((h, idx) => (
                      <th
                        key={h}
                        className={`px-4 md:px-6 py-4 text-left font-semibold text-foreground-50 ${
                          idx === 0 ? 'w-1/3' : ''
                        }`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparison.rows.map((row, rIdx) => (
                    <tr
                      key={row[0]}
                      className={`${
                        rIdx % 2 === 0
                          ? 'bg-background-50'
                          : 'bg-background-100/50'
                      } border-t border-background-200/30`}
                    >
                      <td className="px-4 md:px-6 py-4 text-foreground-300 font-medium">
                        {row[0]}
                      </td>
                      <td className="px-4 md:px-6 py-4 text-foreground-100">
                        {row[1]}
                      </td>
                      <td className="px-4 md:px-6 py-4 text-foreground-400">
                        {row[2]}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* 服务实力统计 */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading">
                关联单位、合作生态圈资源
              </h2>
              <div className="mt-4 mx-auto w-12 h-0.5 bg-primary-500 rounded-full" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5 md:gap-6">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="group flex flex-col items-center text-center bg-background-100 rounded-lg border border-background-200/50 p-6 md:p-8 hover:border-primary-500/40 transition-all duration-300"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500 mb-4 group-hover:bg-primary-500 group-hover:text-foreground-50 transition-colors">
                    <i className={`${stat.icon} text-xl`} />
                  </div>
                  <span className="text-2xl md:text-3xl font-black text-primary-500 font-heading">
                    {stat.value}
                  </span>
                  <span className="mt-2 text-sm text-foreground-300">
                    {stat.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 其它产品 */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="w-full px-4 md:px-8 lg:px-12 xl:px-20 max-w-[1400px] mx-auto">
            <div className="flex items-end justify-between">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading">
                  其它产品
                </h2>
                <div className="mt-4 w-12 h-0.5 bg-primary-500 rounded-full" />
              </div>
              <Link
                to="/product-center"
                className="inline-flex items-center gap-2 text-sm font-medium text-foreground-400 hover:text-primary-400 transition-colors whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {otherProducts.map((item) => (
                <Link
                  key={item.slug}
                  to={`/product-center/${item.slug}`}
                  className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/50 hover:border-primary-500/40 transition-all duration-300"
                >
                  <div className="relative w-full h-44 overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.titleCn}
                      title={`${item.titleCn} ${item.titleEn}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute bottom-3 left-4 w-10 h-10 flex items-center justify-center rounded-lg bg-background-50/90 text-primary-500">
                      <i className={`${item.icon} text-lg`} />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-base md:text-lg font-bold text-foreground-50 group-hover:text-primary-400 transition-colors">
                      {item.titleCn}
                    </h3>
                    <p className="mt-2 text-sm text-foreground-300 leading-relaxed line-clamp-2">
                      {item.desc}
                    </p>
                    <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-foreground-400 group-hover:text-primary-400 transition-colors">
                      <span>查看详情</span>
                      <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}